import { useEffect, useState } from "react";
import { Check, Palette, RotateCcw, Sliders } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CUSTOMIZABLE_TOKENS, type CustomMap, type CustomToken } from "./themeConstants";
import {
  ADMIN_THEME_PRESETS,
  adminTokenDefault,
  buildAdminThemeStyle,
  type AdminTheme,
} from "./adminTheme";

export function AdminThemeSwitcher({
  theme,
  onChange,
  compact = false,
}: {
  theme: AdminTheme;
  onChange: (theme: AdminTheme) => void;
  compact?: boolean;
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const active = ADMIN_THEME_PRESETS.find((t) => t.id === theme.preset) ?? ADMIN_THEME_PRESETS[0];
  const scopedStyle = buildAdminThemeStyle(theme);

  function pick(id: string) {
    onChange({ ...theme, preset: id });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8">
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded-full border border-border"
                style={{ background: active.swatch, boxShadow: `0 0 8px ${active.swatch}` }}
              />
              <Palette className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Theme admin</span>
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded-full border border-border"
                style={{ background: active.swatch, boxShadow: `0 0 10px ${active.swatch}` }}
              />
              <Palette className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Theme admin</span>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="admin-shell w-56" data-admin-appearance={theme.appearance} style={scopedStyle}>
          <DropdownMenuLabel className="text-xs">Màu Admin</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-72 overflow-y-auto">
            {ADMIN_THEME_PRESETS.map((t) => (
              <DropdownMenuItem key={t.id || "default"} onClick={() => pick(t.id)} className="gap-2">
                <span
                  aria-hidden
                  className="h-3.5 w-3.5 rounded-full border border-border"
                  style={{ background: t.swatch, boxShadow: `0 0 8px ${t.swatch}` }}
                />
                <span className="flex-1">{t.label}</span>
                {theme.preset === t.id && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setCustomOpen(true); }} className="gap-2">
            <Sliders className="h-3.5 w-3.5" />
            <span className="flex-1">Tùy chỉnh màu Admin...</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AdminCustomThemeDialog theme={theme} onChange={onChange} open={customOpen} onOpenChange={setCustomOpen} />
    </>
  );
}

function AdminCustomThemeDialog({
  theme,
  onChange,
  open,
  onOpenChange,
}: {
  theme: AdminTheme;
  onChange: (theme: AdminTheme) => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [values, setValues] = useState<CustomMap>({});

  useEffect(() => {
    if (open) setValues(theme.custom ?? {});
  }, [open, theme.custom]);

  function commit(next: CustomMap) {
    setValues(next);
    onChange({ ...theme, custom: next });
  }

  function update(key: CustomToken, val: string) {
    const next = { ...values, [key]: val };
    if (!val.trim()) delete next[key];
    commit(next);
  }

  function reset(key: CustomToken) {
    const next = { ...values };
    delete next[key];
    commit(next);
  }

  function resetAll() {
    commit({});
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-shell max-w-2xl" data-admin-appearance={theme.appearance} style={buildAdminThemeStyle(theme)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sliders className="h-4 w-4" /> Theme admin
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1">
          Các màu này chỉ áp dụng trong admin panel của tài khoản admin hiện tại, không đổi public/main page.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {CUSTOMIZABLE_TOKENS.map((t) => {
            const current = values[t.key] ?? "";
            const display = current || adminTokenDefault(t.key, theme.appearance);
            return (
              <div key={t.key} className="flex items-center gap-2 rounded-md border border-border/60 p-2">
                <input
                  type="color"
                  value={display.startsWith("#") ? display : adminTokenDefault(t.key, theme.appearance)}
                  onChange={(e) => update(t.key, e.target.value)}
                  className="h-8 w-10 rounded cursor-pointer border border-border/60 bg-transparent"
                  aria-label={t.label}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{t.label}</div>
                  <input
                    type="text"
                    value={current}
                    onChange={(e) => update(t.key, e.target.value)}
                    placeholder={adminTokenDefault(t.key, theme.appearance)}
                    className="w-full bg-transparent text-[11px] text-muted-foreground outline-none"
                  />
                </div>
                {current && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0"
                    onClick={() => reset(t.key)}
                    title="Khôi phục mặc định"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={resetAll}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Khôi phục tất cả
          </Button>
          <Button onClick={() => onOpenChange(false)}>Xong</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}